import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ThreeDotsButton from "../../atoms/ThreeDotsButton";

export default function BandBoardListActions({ list, onEdit = undefined, onDelete = undefined }) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const firstItemRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handlePointerDown = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && firstItemRef.current) {
            firstItemRef.current.focus();
        }
    }, [isOpen]);

    if (!onEdit && !onDelete) {
        return null;
    }

    const toggleMenu = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen((current) => !current);
    };

    const handleSelect = (callback) => (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
        callback?.(list);
    };

    return (
        <div className="band-board__list-actions" ref={containerRef}>
            <ThreeDotsButton
                className="band-board__list-trigger"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={t("bandsPage.board.listActions.aria", { name: list?.name || t("bandsPage.board.listActions.fallback") })}
                onClick={toggleMenu}
            />
            {isOpen && (
                <div className="band-board__list-menu" role="menu">
                    {onEdit && (
                        <button
                            type="button"
                            role="menuitem"
                            ref={firstItemRef}
                            onClick={handleSelect(onEdit)}
                        >
                            {t("bandsPage.board.listActions.edit")}
                        </button>
                    )}
                    {onDelete && (
                        <button
                            type="button"
                            role="menuitem"
                            onClick={handleSelect(onDelete)}
                        >
                            {t("bandsPage.board.listActions.delete")}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

BandBoardListActions.propTypes = {
    list: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        cards: PropTypes.array,
        position: PropTypes.number,
    }).isRequired,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
};
